import React, { createContext, useCallback, useContext, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button, IconButton } from './Button';
import { CloseIcon, WarningIcon } from './icons';

const FOCUSABLE = [
  'a[href]', 'area[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])', 'textarea:not([disabled])', 'iframe', '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',');

export const focusableIn = (node) => Array.from(node.querySelectorAll(FOCUSABLE))
  .filter((el) => !el.hasAttribute('inert') && el.getAttribute('aria-hidden') !== 'true');

// Open dialogs, innermost last: only it reacts to Esc and Tab.
const stack = [];
let scrollLocks = 0;

/**
 * Keeps keyboard focus inside `ref` while `active`: focuses the initial
 * element, wraps Tab / Shift+Tab, closes on Esc (innermost layer only),
 * locks page scroll, and gives focus back to what had it before.
 * Used by Dialog and by the shell's drawer.
 */
export function useModalFocus(active, ref, { onClose, initialFocusRef } = {}) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!active || !ref.current) return undefined;
    const panel = ref.current;
    const previous = document.activeElement;
    stack.push(panel);
    scrollLocks += 1;
    document.body.classList.add('has-modal');

    const target = (initialFocusRef && initialFocusRef.current) || focusableIn(panel).find((el) => !el.dataset.dialogClose) || panel;
    target.focus();

    // Esc is heard in the bubble phase, after the controls inside: a menu or a
    // search field that used it (preventDefault) keeps the layer open.
    const onEscape = (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (stack[stack.length - 1] !== panel) return;
      event.preventDefault();
      if (onCloseRef.current) onCloseRef.current();
    };
    const onKeyDown = (event) => {
      if (stack[stack.length - 1] !== panel) return;
      if (event.key !== 'Tab') return;
      const nodes = focusableIn(panel);
      if (nodes.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!panel.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('keydown', onEscape);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('keydown', onEscape);
      const index = stack.indexOf(panel);
      if (index >= 0) stack.splice(index, 1);
      scrollLocks -= 1;
      if (scrollLocks <= 0) {
        scrollLocks = 0;
        document.body.classList.remove('has-modal');
      }
      if (previous && previous !== document.body && typeof previous.focus === 'function' && document.contains(previous)) {
        previous.focus();
      } else {
        // The opener is gone (e.g. the deleted row's button): the page's main region.
        const main = document.getElementById('main-content');
        if (main) main.focus();
      }
    };
  }, [active, ref, initialFocusRef]);
}

/**
 * A modal dialog: aria-modal, focus kept inside (Tab / Shift+Tab wrap),
 * Esc and the scrim close it, focus returns to what had it before.
 * Renders nothing while `open` is false.
 */
export function Dialog({
  open, onClose, title, description, children, footer, size = 'md',
  initialFocusRef, closeOnScrim = true, role = 'dialog', hideClose = false, className = '',
}) {
  const { t } = useTranslation();
  const panelRef = useRef(null);
  const titleId = useId();
  const descId = useId();
  useModalFocus(open, panelRef, { onClose, initialFocusRef });

  if (!open) return null;

  return createPortal(
    <div
      className="ui-dialog-scrim"
      onMouseDown={(e) => {
        if (closeOnScrim && e.target === e.currentTarget && onClose) onClose();
      }}
    >
      <div
        ref={panelRef}
        role={role}
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className={`ui-dialog ui-dialog--${size} ${className}`.trim()}
      >
        <div className="ui-dialog__header">
          {title && <h2 id={titleId} className="ui-dialog__title">{title}</h2>}
          {!hideClose && onClose && (
            <IconButton icon={CloseIcon} label={t('common.close')} variant="ghost" onClick={onClose} data-dialog-close="1" className="ui-dialog__close" />
          )}
        </div>
        {description && <div id={descId} className="ui-dialog__description">{description}</div>}
        {children != null && <div className="ui-dialog__body">{children}</div>}
        {footer && <div className="ui-dialog__footer">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

/** A yes/no question. Destructive ones (danger) use a danger button that names the action. */
export function ConfirmDialog({ open, title, message, confirmLabel, cancelLabel, danger = false, onConfirm, onCancel }) {
  const { t } = useTranslation();
  const cancelRef = useRef(null);
  const confirmRef = useRef(null);
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      title={title || t('ui.confirmTitle')}
      description={message}
      role="alertdialog"
      size="sm"
      hideClose
      // A destructive question starts on Cancel, so Enter never deletes by accident.
      initialFocusRef={danger ? cancelRef : confirmRef}
      className={danger ? 'ui-dialog--danger' : ''}
      footer={(
        <>
          <Button ref={cancelRef} variant="secondary" onClick={onCancel}>{cancelLabel || t('common.cancel')}</Button>
          <Button ref={confirmRef} variant={danger ? 'danger' : 'primary'} icon={danger ? WarningIcon : undefined} onClick={onConfirm}>
            {confirmLabel || t('ui.confirm')}
          </Button>
        </>
      )}
    />
  );
}

const ConfirmContext = createContext(null);

/**
 * Provides useConfirm(): `await confirm({title, message, confirmLabel, danger})`
 * resolves to true or false. A plain string is taken as the message.
 */
export function ConfirmProvider({ children }) {
  const [request, setRequest] = useState(null);
  const resolver = useRef(null);

  const confirm = useCallback((options) => {
    const opts = typeof options === 'string' ? { message: options } : (options || {});
    if (resolver.current) resolver.current(false);
    return new Promise((resolve) => {
      resolver.current = resolve;
      setRequest(opts);
    });
  }, []);

  const settle = (value) => {
    const resolve = resolver.current;
    resolver.current = null;
    setRequest(null);
    if (resolve) resolve(value);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmDialog
        open={!!request}
        title={request?.title}
        message={request?.message}
        confirmLabel={request?.confirmLabel}
        cancelLabel={request?.cancelLabel}
        danger={request?.danger}
        onConfirm={() => settle(true)}
        onCancel={() => settle(false)}
      />
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const confirm = useContext(ConfirmContext);
  if (!confirm) throw new Error('useConfirm needs a <ConfirmProvider> (see ui/UIProvider).');
  return confirm;
}

export default Dialog;
